import {
    accordionOpenedStatusFromSections,
    AddressState,
    ArrayModel,
    CalibrationModel,
    complaintsToJson,
    ConfigConstants,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    ConfirmationModelFromJson,
    ConfirmationSignatureSnapshotState,
    ConfirmationState,
    ConfirmationSummaryModel,
    convertTimesheetStateToTimesheetModel,
    equipmentToJson,
    expensesToJson,
    followupToJson,
    fromJsonConfirmationModel,
    installationPaperworkToJson,
    isDefined,
    JsonConfirmationModel,
    JsonMaterialModel,
    MaterialModel,
    MaterialService,
    measurementToolsToJson,
    measuringPointsToJson,
    NOTIFICATION_STATUS_CODE,
    NotificationIdentifierModel,
    NotificationIdState,
    ODataCalibrationModel,
    ODataObject,
    participantsToJson,
    serviceReportToJson,
    sourceReplacementsToJson,
    StatusModel,
    StoredLegacyConfirmation,
    timeRecordingToJson,
    TIMESHEET_KEYS,
    TimesheetModel,
    TimesheetState,
    uploadToDocboxToJson,
} from '@evorait/pace-core';
import moment from 'moment';

/**
 * Every converter that touches a legacy model class lives here. Nothing under `state/` — actions,
 * reducers, selectors, effects — may import or instantiate one (see `architecture-rules.md`); when
 * an effect has to hand a model to a non-NgRx collaborator it builds it from a converter in this
 * file rather than inline. That keeps the whole model surface of the confirmation slice in one
 * place, and keeps `ConfirmationNgrxService` a selector read plus one converter call.
 */

/** Reduce a {@link NotificationIdentifierModel} to the three id fields the store keeps. */
export function notificationIdToState(notificationId: NotificationIdentifierModel): NotificationIdState {
    return { frontId: notificationId.frontId, qmNum: notificationId.qmNum, maNum: notificationId.maNum };
}

/** Rebuild a {@link NotificationIdentifierModel} from its stored id fields. */
export function notificationIdFromState(notificationId: NotificationIdState): NotificationIdentifierModel {
    return new NotificationIdentifierModel(notificationId.frontId, notificationId.qmNum, notificationId.maNum);
}

/** Rehydrates the stored timesheet slice into the `TimesheetModel` the confirmation models require. */
function toTimesheetModel(
    timesheet: TimesheetState,
    addresses: readonly AddressState[],
    userCountry: string,
): TimesheetModel {
    return convertTimesheetStateToTimesheetModel(timesheet, TIMESHEET_KEYS.CURRENT_TIMESHEET, addresses, userCountry);
}

/**
 * What the slice needs, besides a stored confirmation, to rebuild it into a {@link ConfirmationModel}:
 * the current timesheet, the address collection its entries' address ids resolve against, and the
 * country every timesheet address carries.
 */
export type ConfirmationRehydrationContext = Readonly<{
    timesheet: TimesheetState;
    addresses: readonly AddressState[];
    userCountry: string;
}>;

/**
 * Takes the material list off the live model, for the save to carry beside the confirmation. The
 * `JSON.stringify`/`JSON.parse` round-trip is the one storage applies, so the result is plain data an action
 * may carry, and what `MaterialService.materialsFromJson` reads.
 *
 * @param model the confirmation being saved
 * @returns its materials, or `undefined` when it has no material section
 */
export function modelToMaterialsSnapshot(model: ConfirmationModel): JsonMaterialModel[] | undefined {
    if (!isDefined(model.materials)) {
        return undefined;
    }
    return JSON.parse(JSON.stringify(model.materials.listMaterial.value)) as JsonMaterialModel[];
}

/**
 * Takes what the technician and the customer signed off the summary, for the submit to carry beside the
 * confirmation. `ConfirmationComponent` attaches the summary to the live model when it opens it for
 * signing, so before that there is nothing to take.
 *
 * @param summary the model's summary, absent until it was opened for signing (and always for PACE US)
 */
export function summaryToSignatureSnapshot(
    summary: ConfirmationSummaryModel | undefined,
): ConfirmationSignatureSnapshotState | undefined {
    if (!isDefined(summary)) {
        return undefined;
    }
    return {
        customerName: summary.customerName.value,
        customerSignature: summary.customerSignature.value,
        // Both fields only exist when config shows them.
        cseSignature: summary.cseSignature?.value,
        customerContact: summary.customerContact?.value,
        signedAt: summary.signedDate.value.toISOString(),
    };
}

/**
 * Converts a live {@link ConfirmationModel} into the plain confirmation the slice stores, stamping
 * `timestamp` as the save time. The model is flattened with a `JSON.stringify`/`JSON.parse` round-trip
 * over the model itself (not just `model.toJSON()`), so nested model instances (equipment, materials,
 * time-recording, ...) become plain data — which is exactly what the legacy `StorageConfirmationService`
 * stored. That makes {@link fromJsonConfirmationModel} the conversion for both: a save goes through the
 * same code as a legacy confirmation the migration reads.
 *
 * @param model the confirmation to persist
 * @param timestamp the save timestamp to stamp on it
 */
export function modelToConfirmationState(model: ConfirmationModel, timestamp: number): ConfirmationState {
    return fromJsonConfirmationModel(
        JSON.parse(JSON.stringify(model)) as StoredLegacyConfirmation,
        model.type,
        timestamp,
    );
}

/**
 * Re-selects the time-recording billable preset from its stored code.
 *
 * This is the one place where the migration does not simply hand the legacy restore what it used to read.
 * That restore matched the preset by its **translated display name**
 * (`confirmation-time-recording.model.ts:581-583`), so a technician who changed language between saving
 * and loading lost the preset; the stored code does not depend on the UI language. The payload therefore
 * omits `billablePresetValue`, which skips the display-name match, and the selection is applied here
 * through the same public dropdown API the model itself uses.
 *
 * @param model the freshly rehydrated confirmation
 * @param code the stored preset code, absent when nothing was selected
 */
function applyBillablePreset(model: ConfirmationModel, code: string | undefined): void {
    if (isDefined(code) && model.timeRecording) {
        model.timeRecording.billablePresetModel.selectByValue(code);
    }
}

/**
 * Re-adds the punch-list entries the technician typed.
 *
 * The payload hands the section empty punch lists on purpose: the section's restore replaces each list
 * wholesale, and re-adding the entries here goes through its own `addPunchlistItem`, so every entry gets
 * the validation the model defines (a maximum length, a three-character minimum, required) instead of a
 * copy of those rules living in the app.
 *
 * @param model the freshly rehydrated confirmation
 * @param stored the stored confirmation, holding the entry texts
 */
function applyPunchLists(model: ConfirmationModel, stored: ConfirmationState): void {
    const paperwork = model.installationPaperwork;
    const paperworkState = stored.installationPaperwork;
    if (!paperwork || !paperworkState) {
        return;
    }
    (paperworkState.siemensPunchList?.entries ?? []).forEach((entry) =>
        paperwork.addPunchlistItem(paperwork.siemensPunchlist, entry),
    );
    (paperworkState.customerPunchList?.entries ?? []).forEach((entry) =>
        paperwork.addPunchlistItem(paperwork.customerPunchlist, entry),
    );
}

/**
 * Re-selects the reason the technician documented for submitting without a protocol.
 *
 * `ConfirmationModelFromJson` builds the reason model fresh, with nothing selected, and the legacy
 * implementation never noticed because it submitted the live model. The NgRx submit rebuilds the model
 * from the stored confirmation, so without this the OData payload goes out without the reason's CRM code.
 *
 * @param model the freshly rehydrated confirmation
 * @param stored the stored reason, absent when none was documented
 */
function applyReasonMissingProtocols(
    model: ConfirmationModel,
    stored: ConfirmationState['reasonMissingProtocols'],
): void {
    if (isDefined(stored) && model.reasonMissingProtocols) {
        model.reasonMissingProtocols.restoreReason(stored.reasonCode, stored.otherReasonDescription);
    }
}

/**
 * Rehydrates a stored confirmation back into a live {@link ConfirmationModel} via
 * `ConfirmationModelFromJson`. The save time needs no correction afterwards: the stored
 * `saveTimestamp` *is* the write time, stamped by {@link modelToConfirmationState}, and the
 * constructor restores the model from it.
 *
 * The stored form holds business data, so the migrated sections are widened back to the shape each
 * section's own restore reads before the model sees them; the sections still stored as legacy JSON pass
 * through untouched. The accordion flags are collected back off the sections into the one object the model
 * restores them from: the constructor applies each one only for a section it actually builds, so a flag
 * stored for a section this confirmation no longer has is dropped there rather than forced onto the model
 * here.
 *
 * @param stored the stored confirmation
 * @param timesheet the current timesheet, required by `ConfirmationModelFromJson`
 * @param addresses the address collection a stored timesheet entry's address id is resolved against
 * @param userCountry the country every timesheet address carries, from user master
 */
export function confirmationStateToModel(
    stored: ConfirmationState,
    timesheet: TimesheetState,
    addresses: readonly AddressState[],
    userCountry: string,
): ConfirmationModel {
    const payload = {
        ...stored,
        // `ConfirmationModelFromJson` reads the identifier under the legacy name, so it is widened
        // back here alongside the sections.
        notificationIdentifier: stored.notificationId,
        equipment: equipmentToJson(stored.equipment),
        uploadToDocBox: uploadToDocboxToJson(stored.uploadToDocBox),
        complaints: complaintsToJson(stored.complaints),
        followup: followupToJson(stored.followup),
        measuringPoints: measuringPointsToJson(stored.measuringPoints),
        participants: participantsToJson(stored.participants),
        sourceReplacements: sourceReplacementsToJson(stored.sourceReplacements),
        expenses: expensesToJson(stored.expenses),
        serviceReport: serviceReportToJson(stored.serviceReport),
        measurementTool: measurementToolsToJson(stored.measurementTool),
        timeRecording: timeRecordingToJson(stored.timeRecording),
        installationPaperwork: installationPaperworkToJson(stored.installationPaperwork),
        accordionOpenedStatus: accordionOpenedStatusFromSections(stored),
    } as unknown as JsonConfirmationModel;
    const model = new ConfirmationModelFromJson(
        payload,
        stored.confirmationType,
        toTimesheetModel(timesheet, addresses, userCountry),
    );
    applyBillablePreset(model, stored.timeRecording?.billablePresetCode);
    applyPunchLists(model, stored);
    applyReasonMissingProtocols(model, stored.reasonMissingProtocols);
    return model;
}

/**
 * The OData fields the legacy `ConfirmationSummaryModel.getOutModel()` produced, under the same names and in
 * the same format. A field config did not show is left out, as the summary never set it.
 *
 * @param snapshot what was signed, absent when the summary was never opened (and always for PACE US)
 */
function signatureSnapshotToOutModel(snapshot: ConfirmationSignatureSnapshotState | undefined): ODataObject {
    if (!isDefined(snapshot)) {
        return {};
    }
    return {
        CustomerName: snapshot.customerName,
        CustomerSignature: snapshot.customerSignature,
        SignDateCse: moment(snapshot.signedAt).format(ConfigConstants.formatDateNoTimezone),
        ...(isDefined(snapshot.cseSignature) ? { CSESignature: snapshot.cseSignature } : {}),
        ...(isDefined(snapshot.customerContact) ? { CustomerContact: snapshot.customerContact } : {}),
    };
}

/**
 * Builds the whole OData payload a submit sends: the confirmation's sections plus what was signed.
 *
 * TODO: the section payloads still come from the legacy models' `getOutModel()`, so the confirmation is
 * rebuilt for them. They are to be built from the state directly, section by section; the signature has no
 * model behind it any more, since the summary's fields are mapped here. A plain spread is enough for it: no
 * section uses the summary's keys.
 *
 * @param confirmation the confirmation to submit
 * @param signatureSnapshot what was signed on the summary, absent when it was never opened
 * @param materials the submitted materials, absent when the confirmation has no material section
 * @param context what the rebuild needs besides the confirmation
 */
export function confirmationStateToOutModel(
    confirmation: ConfirmationState,
    signatureSnapshot: ConfirmationSignatureSnapshotState | undefined,
    materials: MaterialModel[] | undefined,
    context: ConfirmationRehydrationContext,
): ODataObject {
    const model = confirmationStateToModel(confirmation, context.timesheet, context.addresses, context.userCountry);
    // The rebuilt section fills its list from `MaterialService`, whose instances a sync may have replaced
    // with pre-edit copies; the submitted list is what the technician left.
    if (isDefined(model.materials)) {
        if (isDefined(materials)) {
            model.materials.listMaterial = new ArrayModel<MaterialModel>('MaterialSet', materials);
        } else {
            model.materials = undefined;
        }
    }
    return {
        ...model.getOutModel(),
        ...signatureSnapshotToOutModel(signatureSnapshot),
    };
}

/**
 * Builds the model for a confirmation that was never stored — the counterpart to
 * {@link confirmationStateToModel}, taking the same plain `TimesheetState` so callers never have to
 * rehydrate the timesheet themselves.
 *
 * @param id the notification the confirmation belongs to
 * @param type the confirmation type
 * @param timesheet the current timesheet slice, required by `ConfirmationModel`
 * @param addresses the address collection a stored timesheet entry's address id is resolved against
 * @param userCountry the country every timesheet address carries, from user master
 */
function newConfirmationModel(
    id: NotificationIdentifierModel,
    type: CONFIRMATION_TYPES,
    timesheet: TimesheetState,
    addresses: readonly AddressState[],
    userCountry: string,
): ConfirmationModel {
    return new ConfirmationModel(id, type, toTimesheetModel(timesheet, addresses, userCountry));
}

/**
 * Merges the notification's locally stored materials into `model`'s material list, mirroring what
 * the legacy `ConfirmationService.getConfirmation` did inline on every load. Applies to a
 * rehydrated and a freshly built model alike: material history is keyed by notification, so it can
 * exist even for a confirmation that was never saved.
 *
 * `materialService` is passed in rather than injected because converters are plain functions with
 * no DI of their own.
 *
 * @param model the confirmation model to enrich, modified in place
 * @param materialService the collaborator owning the local material store
 * @returns the same `model`, for call-site chaining
 */
function hydrateConfirmationModel(model: ConfirmationModel, materialService: MaterialService): ConfirmationModel {
    // check for confirmation.materials as it could be undefined for APPS user
    if (model.materials && model.materials.listMaterial) {
        model.materials.listMaterial = materialService.updateMaterialListFromLocalStorage(
            model.notificationIdentifier,
            model.materials.listMaterial,
        );
    }
    return model;
}

/**
 * Translates one confirmation read — the stored confirmation (or its absence) plus the timesheet
 * slice — into the live {@link ConfirmationModel} a `getConfirmation` caller receives: the stored one
 * rehydrated when it exists, a fresh model otherwise, with the notification's locally stored materials
 * merged in either way.
 *
 * This is the whole result translation for `ConfirmationNgrxService.getConfirmation`, so that
 * method stays a selector read plus a single converter call, per `architecture-rules.md`.
 *
 * @param stored the stored confirmation, or `undefined` when it has never been saved
 * @param id the notification the confirmation belongs to
 * @param type the confirmation type
 * @param timesheet the current timesheet slice
 * @param materialService the collaborator owning the local material store
 * @param addresses the address collection a stored timesheet entry's address id is resolved against
 * @param userCountry the country every timesheet address carries, from user master
 */
export function stateToConfirmationModel(
    stored: ConfirmationState | undefined,
    id: NotificationIdentifierModel,
    type: CONFIRMATION_TYPES,
    timesheet: TimesheetState,
    materialService: MaterialService,
    addresses: readonly AddressState[],
    userCountry: string,
): ConfirmationModel {
    const model = stored
        ? confirmationStateToModel(stored, timesheet, addresses, userCountry)
        : newConfirmationModel(id, type, timesheet, addresses, userCountry);
    return hydrateConfirmationModel(model, materialService);
}

/**
 * Builds the {@link CalibrationModel} a `getCalibrationDate` caller receives from the raw OData
 * record the outcome action carries.
 *
 * @param calibration the raw OData calibration record
 */
export function calibrationFromOData(calibration: ODataCalibrationModel): CalibrationModel {
    return CalibrationModel.fromODataCalibrationDate(calibration);
}

// ---- Models built for the submit pipeline's collaborators --------------------------------------
// `ConfirmationSubmitEffects` has to hand real models to `NotificationService`, `MileageService`
// and `TimesheetAndExpensesInterface`, whose signatures demand them. The effect builds them
// through these converters rather than inline, so no `new …Model(...)` appears under `state/`.

/**
 * Builds the {@link StatusModel} `NotificationService.saveStatus` expects for a status transition.
 * Both timestamps are the same instant, matching every legacy call site.
 *
 * @param statusCode the status to move the notification to
 * @param now the transition timestamp, passed in so a caller can share one instant across steps
 */
export function statusModelFor(statusCode: NOTIFICATION_STATUS_CODE, now: moment.Moment): StatusModel {
    return new StatusModel(statusCode, now, now);
}
